import { 
  collection, 
  doc, 
  addDoc,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  arrayUnion,
  arrayRemove,
  Timestamp,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Organization, OrganizationMembership, User } from '../types';
import { permissionService } from './permissionService';
import { demoDataService } from './demoDataService';

export const organizationService = {
  // Get current user's organization for testing
  async getCurrentUserOrganization(userId: string, userOrgId: string): Promise<{ organization: Organization | null, users: User[], orgId: string } | null> {
    if (!userOrgId) return null;
    
    try {
      console.log(`Getting organization for current user: ${userId}, orgId: ${userOrgId}`);
      
      // Get the organization document
      const orgDoc = await getDoc(doc(db, 'organizations', userOrgId));
      
      let organization: Organization | null = null;
      
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        organization = {
          id: orgDoc.id,
          name: orgData.name || `Organization ${userOrgId.slice(0, 8)}`,
          ...orgData,
          createdAt: orgData.createdAt?.toDate() || new Date(),
          updatedAt: orgData.updatedAt?.toDate() || new Date()
        } as Organization;
      } else {
        // Create a minimal organization
        organization = {
          id: userOrgId,
          name: userOrgId === 'sIvlOu3I2TbIgMkvUgeZcxUBorf1' ? 'Echomotion' : `Organization ${userOrgId.slice(0, 8)}`,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: userId,
          subscription: {
            plan: 'free',
            status: 'active'
          }
        } as Organization;
      }
      
      // For now, just return the current user as the only user
      return {
        organization,
        users: [{
          id: userId,
          orgId: userOrgId,
          name: 'Current User',
          email: '',
          role: 'admin',
          specialties: []
        } as User],
        orgId: userOrgId
      };
    } catch (error) {
      console.error('Error getting current user organization:', error);
      return null;
    }
  },

  // Get all unique orgIds from users and ensure organizations exist
  async getAllOrganizationsWithUsers(): Promise<{ organization: Organization | null, users: User[], orgId: string }[]> {
    try {
      console.log('getAllOrganizationsWithUsers: Starting...');
      
      // Known organizations with CORRECT org IDs (after migration)
      const knownOrgs = [
        { 
          // Correct Echomotion org ID
          orgId: 'org_echomotion_001', 
          name: 'Echomotion',
          users: [
            { id: 'sIvlOu3I2TbIgMkvUgeZcxUBorf1', name: 'Benjamin Lange', email: 'benjamin@echomotion.de', role: 'admin' },
            { id: 'ykC2iYrVOsN9aoCvPn5feehNynf1', name: 'Moritz Weinert', email: 'moritz@echomotion.de', role: 'admin' }
          ]
        },
        {
          // Correct Van Doorn org ID
          orgId: 'org_vandoorn_001',
          name: 'Van Doorn Company',
          users: [
            { id: 'u12qDyrPWdbhCjxIQaFMealmJjH2', name: 'Jan van Doorn', email: 'jan@echomotion.de', role: 'sales_rep' }
          ]
        },
        // Also support OLD incorrect orgIds during transition period
        { 
          // OLD incorrect orgId (Benjamin's userId)
          orgId: 'sIvlOu3I2TbIgMkvUgeZcxUBorf1', 
          name: 'Echomotion (OLD - needs migration)',
          users: [
            { id: 'sIvlOu3I2TbIgMkvUgeZcxUBorf1', name: 'Benjamin Lange', email: 'benjamin@echomotion.de', role: 'admin' },
            { id: 'ykC2iYrVOsN9aoCvPn5feehNynf1', name: 'Moritz Weinert', email: 'moritz@echomotion.de', role: 'admin' }
          ]
        },
        {
          // OLD incorrect orgId (Jan's userId)
          orgId: 'u12qDyrPWdbhCjxIQaFMealmJjH2',
          name: 'Van Doorn (OLD - needs migration)',
          users: [
            { id: 'u12qDyrPWdbhCjxIQaFMealmJjH2', name: 'Jan van Doorn', email: 'jan@echomotion.de', role: 'sales_rep' }
          ]
        }
      ];
      
      // Try to get all users from Firebase
      let allUsers: User[] = [];
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        console.log(`Found ${usersSnapshot.size} users in Firebase`);
        
        allUsers = usersSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          };
        }) as User[];
        
        // Log summary
        const orgSummary = new Map<string, number>();
        allUsers.forEach(u => {
          if (u.orgId) {
            orgSummary.set(u.orgId, (orgSummary.get(u.orgId) || 0) + 1);
          }
        });
        console.log('User distribution by orgId:', Array.from(orgSummary.entries()).map(([id, count]) => `${id.slice(0, 8)}...: ${count} users`).join(', '));
      } catch (error) {
        console.error('Could not read users from Firebase (permission denied?):', error);
        console.log('Using known users as fallback...');
        
        // Use known users as fallback
        allUsers = knownOrgs.flatMap(org => 
          org.users.map(u => ({
            ...u,
            orgId: org.orgId,
            specialties: []
          } as User))
        );
      }

      // Group users by orgId
      const usersByOrgId = new Map<string, User[]>();
      allUsers.forEach(user => {
        if (user.orgId) {
          const existing = usersByOrgId.get(user.orgId) || [];
          usersByOrgId.set(user.orgId, [...existing, user]);
        }
      });
      
      console.log(`Grouped into ${usersByOrgId.size} unique orgIds`);

      // Load organizations for each unique orgId
      const organizationsWithUsers: { organization: Organization | null, users: User[], orgId: string }[] = [];
      
      for (const [orgId, users] of usersByOrgId.entries()) {
        try {
          // Try to get the organization document
          const orgDoc = await getDoc(doc(db, 'organizations', orgId));
          
          if (orgDoc.exists()) {
            // Organization exists
            const orgData = orgDoc.data();
            const organization: Organization = {
              id: orgDoc.id,
              name: orgData.name || `Organization ${orgId.slice(0, 8)}`,
              domain: orgData.domain,
              industry: orgData.industry,
              size: orgData.size || 'small',
              logo: orgData.logo,
              address: orgData.address,
              phone: orgData.phone,
              email: orgData.email,
              website: orgData.website,
              createdAt: orgData.createdAt?.toDate() || new Date(),
              updatedAt: orgData.updatedAt?.toDate() || new Date(),
              createdBy: orgData.createdBy || users[0]?.id || 'system',
              settings: orgData.settings,
              subscription: orgData.subscription || {
                plan: 'free',
                status: 'active',
                seats: users.length
              }
            };
            organizationsWithUsers.push({ organization, users, orgId });
          } else {
            // Organization doesn't exist - create a minimal entry
            // Find the first admin user in this org
            const adminUser = users.find(u => u.role === 'admin') || users[0];
            
            // Determine a better default name based on known orgs
            let defaultName = `Organization ${orgId.slice(0, 8)}`;
            
            // Special cases for known orgIds
            if (orgId === 'sIvlOu3I2TbIgMkvUgeZcxUBorf1') {
              defaultName = 'Echomotion';
            } else if (orgId === 'u12qDyrPWdbhCjxIQaFMealmJjH2') {
              defaultName = 'Van Doorn Organization';
            }
            
            // Create organization document
            const newOrg: Organization = {
              id: orgId,
              name: defaultName,
              createdAt: new Date(),
              updatedAt: new Date(),
              createdBy: adminUser?.id || 'system',
              subscription: {
                plan: 'free',
                status: 'active',
                seats: users.length
              }
            };
            
            // Try to save to Firebase - if it fails, still show the org
            try {
              await setDoc(doc(db, 'organizations', orgId), {
                ...newOrg,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              });
            } catch (saveError) {
              console.error(`Failed to create organization document for ${orgId}:`, saveError);
              // Continue anyway - show the org even if we couldn't save it
            }
            
            organizationsWithUsers.push({ organization: newOrg, users, orgId });
          }
        } catch (error) {
          console.error(`Error processing organization ${orgId}:`, error);
          // Still add with null organization so users are visible
          organizationsWithUsers.push({ 
            organization: null, 
            users, 
            orgId 
          });
        }
      }
      
      console.log(`Successfully loaded ${organizationsWithUsers.length} organization(s) with users`);
      return organizationsWithUsers;
    } catch (error) {
      console.error('Error getting all organizations with users:', error);
      // Return empty array instead of throwing
      return [];
    }
  },

  // Create a new organization
  async createOrganization(
    orgData: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>, 
    userId: string
  ): Promise<string> {
    try {
      const batch = writeBatch(db);
      
      // Create organization document
      const orgRef = doc(collection(db, 'organizations'));
      const newOrg = {
        ...orgData,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscription: orgData.subscription || {
          plan: 'free',
          status: 'active',
          seats: 5
        },
        // Ensure onboardingData is preserved if passed
        onboardingData: orgData.onboardingData || null
      };
      batch.set(orgRef, newOrg);
      
      // If created via onboarding with scraping, create auto-populated offer template
      if (orgData.onboardingData?.websiteUrl) {
        const logoUrl = orgData.logo; // Use selected logo
        
        const address = orgData.address || {
          street: '',
          postalCode: '',
          city: '',
          country: ''
        };

        // Create default header template
        // Firebase doesn't allow undefined values, so use empty strings as fallback
        const templateRef = doc(collection(db, 'offer_templates'));
        const defaultTemplate = {
          name: 'Standard',
          isDefault: true,
          orgId: orgRef.id,
          createdBy: userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          seller: {
            name: orgData.name || '',
            logo: logoUrl || '',
            headline: orgData.onboardingData?.slogan || orgData.onboardingData?.usp || '',
            address,
            email: orgData.email || '',
            phone: orgData.phone || '',
            website: orgData.website || ''
          }
        };
        batch.set(templateRef, defaultTemplate);
      }
      
      // Create organization settings document
      const settingsRef = doc(db, 'settings', orgRef.id);
      batch.set(settingsRef, {
        orgId: orgRef.id,
        industries: [
          'Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 
          'Retail & E-Commerce', 'Education', 'Real Estate', 'Professional Services',
          'Media & Entertainment', 'Transportation & Logistics', 'Energy & Utilities',
          'Agriculture', 'Non-Profit', 'Government', 'Telecommunications', 
          'Hospitality', 'Construction', 'Pharmaceutical', 'Automotive', 'Other'
        ],
        contactStages: ['untouched', 'contacted', 'interested', 'qualified', 'converted'],
        opportunityStages: ['prospecting', 'qualification', 'proposal', 'negotiation', 'closing', 'won', 'lost'],
        updatedAt: serverTimestamp(),
        updatedBy: userId
      });
      
      // Create organization config for stages
      const configRef = doc(db, 'orgConfigs', orgRef.id, 'settings', 'stages');
      batch.set(configRef, {
        leadStages: {
          untouched: { order: 0, color: 'gray' },
          contacted: { order: 1, color: 'blue' },
          interested: { order: 2, color: 'green' },
          qualified: { order: 3, color: 'purple' },
          converted: { order: 4, color: 'yellow' }
        },
        opportunityStages: {
          prospecting: { order: 0, color: 'gray' },
          qualification: { order: 1, color: 'blue' },
          proposal: { order: 2, color: 'indigo' },
          negotiation: { order: 3, color: 'purple' },
          closing: { order: 4, color: 'orange' },
          won: { order: 5, color: 'green' },
          lost: { order: 6, color: 'red' }
        },
        updatedAt: serverTimestamp()
      });
      
      // Commit batch
      await batch.commit();
      
      // Initialize system roles for this organization
      try {
        await permissionService.initializeSystemRoles(orgRef.id, userId);
        console.log(`✅ Initialized system roles for organization ${orgRef.id}`);
      } catch (roleError) {
        console.error('⚠️ Failed to initialize system roles:', roleError);
        // Don't fail organization creation if role initialization fails
      }
      
      // Add creator as admin member
      const membership: OrganizationMembership = {
        orgId: orgRef.id,
        orgName: orgData.name,
        role: 'admin',
        joinedAt: new Date(),
        status: 'active'
      };
      
      // Update user's organization memberships
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizations: arrayUnion(membership),
        orgId: orgRef.id, // Set as current org
        lastActiveOrg: orgRef.id,
        role: 'admin' // Ensure user is admin for their created org
      });
      
      console.log(`✅ Organization ${orgRef.id} created successfully for user ${userId}`);
      
      return orgRef.id;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  // Create a demo organization with sample data
  async createDemoOrganization(
    orgName: string,
    userId: string,
    generateData: boolean = true
  ): Promise<string> {
    try {
      console.log(`🎬 Creating demo organization: ${orgName}`);
      
      const batch = writeBatch(db);
      
      // Create organization document with demo flag
      const orgRef = doc(collection(db, 'organizations'));
      const newOrg = {
        name: orgName,
        isDemo: true,
        demoDataGenerated: false,
        demoCreatedAt: serverTimestamp(),
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        subscription: {
          plan: 'professional', // Demo orgs get professional plan features
          status: 'active',
          seats: 50
        },
        industry: 'Technology',
        size: 'medium' as const
      };
      batch.set(orgRef, newOrg);
      
      // Create organization settings document
      const settingsRef = doc(db, 'settings', orgRef.id);
      batch.set(settingsRef, {
        orgId: orgRef.id,
        industries: [
          'Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 
          'Retail & E-Commerce', 'Education', 'Real Estate', 'Professional Services',
          'Media & Entertainment', 'Transportation & Logistics', 'Energy & Utilities',
          'Agriculture', 'Non-Profit', 'Government', 'Telecommunications', 
          'Hospitality', 'Construction', 'Pharmaceutical', 'Automotive', 'Other'
        ],
        contactStages: ['untouched', 'contacted', 'interested', 'qualified', 'converted'],
        opportunityStages: ['prospecting', 'qualification', 'proposal', 'negotiation', 'closing', 'won', 'lost'],
        updatedAt: serverTimestamp(),
        updatedBy: userId
      });
      
      // Create organization config for stages
      const configRef = doc(db, 'orgConfigs', orgRef.id, 'settings', 'stages');
      batch.set(configRef, {
        leadStages: {
          untouched: { order: 0, color: 'gray' },
          contacted: { order: 1, color: 'blue' },
          interested: { order: 2, color: 'green' },
          qualified: { order: 3, color: 'purple' },
          converted: { order: 4, color: 'yellow' }
        },
        opportunityStages: {
          prospecting: { order: 0, color: 'gray' },
          qualification: { order: 1, color: 'blue' },
          proposal: { order: 2, color: 'indigo' },
          negotiation: { order: 3, color: 'purple' },
          closing: { order: 4, color: 'orange' },
          won: { order: 5, color: 'green' },
          lost: { order: 6, color: 'red' }
        },
        updatedAt: serverTimestamp()
      });
      
      // Commit batch
      await batch.commit();
      
      console.log(`✅ Demo organization ${orgRef.id} structure created`);
      
      // Initialize system roles for this organization
      try {
        await permissionService.initializeSystemRoles(orgRef.id, userId);
        console.log(`✅ Initialized system roles for demo organization ${orgRef.id}`);
      } catch (roleError) {
        console.error('⚠️ Failed to initialize system roles:', roleError);
      }
      
      // Add creator as admin member
      const membership: OrganizationMembership = {
        orgId: orgRef.id,
        orgName,
        role: 'admin',
        joinedAt: new Date(),
        status: 'active'
      };
      
      // Update user's organization memberships
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizations: arrayUnion(membership),
        orgId: orgRef.id, // Set as current org
        lastActiveOrg: orgRef.id,
        role: 'admin'
      });
      
      console.log(`✅ User ${userId} added as admin to demo organization`);
      
      // Generate demo data if requested
      if (generateData) {
        try {
          console.log(`🎨 Generating demo data for organization ${orgRef.id}...`);
          await demoDataService.generateDemoData(orgRef.id, userId);
          
          // Update organization to mark demo data as generated
          await updateDoc(doc(db, 'organizations', orgRef.id), {
            demoDataGenerated: true,
            updatedAt: serverTimestamp()
          });
          
          console.log(`✅ Demo data generated successfully for ${orgRef.id}`);
        } catch (demoError) {
          console.error('⚠️ Failed to generate demo data:', demoError);
          // Don't fail the organization creation if demo data generation fails
        }
      }
      
      console.log(`🎉 Demo organization ${orgRef.id} created successfully!`);
      
      return orgRef.id;
    } catch (error) {
      console.error('❌ Error creating demo organization:', error);
      throw error;
    }
  },

  // Get all organizations for a user
  async getUserOrganizations(userId: string): Promise<Organization[]> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return [];
      
      const userData = userDoc.data() as User;
      
      // Super-Admin can see ALL organizations
      if (userData.isSuperAdmin) {
        console.log('🌍 Super-Admin loading ALL organizations...');
        const orgsSnapshot = await getDocs(collection(db, 'organizations'));
        const allOrgs = orgsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        } as Organization));
        console.log(`✅ Loaded ${allOrgs.length} organizations for Super-Admin`);
        return allOrgs;
      }
      
      // Normal users only see their organizations
      const orgMemberships = userData.organizations || [];
      
      const organizations: Organization[] = [];
      for (const membership of orgMemberships) {
        if (membership.status === 'active') {
          const orgDoc = await getDoc(doc(db, 'organizations', membership.orgId));
          if (orgDoc.exists()) {
            organizations.push({
              id: orgDoc.id,
              ...orgDoc.data(),
              createdAt: orgDoc.data().createdAt?.toDate(),
              updatedAt: orgDoc.data().updatedAt?.toDate()
            } as Organization);
          }
        }
      }
      
      return organizations;
    } catch (error) {
      console.error('Error getting user organizations:', error);
      return [];
    }
  },

  // Get single organization
  async getOrganization(orgId: string): Promise<Organization | null> {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (!orgDoc.exists()) return null;
      
      return {
        id: orgDoc.id,
        ...orgDoc.data(),
        createdAt: orgDoc.data().createdAt?.toDate(),
        updatedAt: orgDoc.data().updatedAt?.toDate()
      } as Organization;
    } catch (error) {
      console.error('Error getting organization:', error);
      return null;
    }
  },

  // Update organization
  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<void> {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      await updateDoc(orgRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // If name changed, update in all user memberships
      if (updates.name) {
        const usersQuery = query(
          collection(db, 'users'),
          where('organizations', 'array-contains', { orgId })
        );
        const usersSnapshot = await getDocs(usersQuery);
        
        const batch = writeBatch(db);
        usersSnapshot.docs.forEach(userDoc => {
          const userData = userDoc.data() as User;
          const updatedMemberships = userData.organizations?.map(m => 
            m.orgId === orgId ? { ...m, orgName: updates.name } : m
          ) || [];
          batch.update(doc(db, 'users', userDoc.id), {
            organizations: updatedMemberships
          });
        });
        await batch.commit();
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  },

  // Add user to organization
  async addUserToOrganization(
    userId: string,
    orgId: string,
    role: 'admin' | 'manager' | 'sales_rep' = 'sales_rep'
  ): Promise<void> {
    try {
      // Get organization details
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (!orgDoc.exists()) throw new Error('Organization not found');
      
      const orgData = orgDoc.data() as Organization;
      
      // Create membership
      const membership: OrganizationMembership = {
        orgId,
        orgName: orgData.name,
        role,
        joinedAt: new Date(),
        status: 'active'
      };
      
      // Update user document
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizations: arrayUnion(membership)
      });
    } catch (error) {
      console.error('Error adding user to organization:', error);
      throw error;
    }
  },

  // Remove user from organization
  async removeUserFromOrganization(userId: string, orgId: string): Promise<void> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data() as User;
      const userRef = doc(db, 'users', userId);
      const remainingOrgs = userData.organizations?.filter(m => m.orgId !== orgId) || [];
      const updates: Record<string, any> = {};

      // Ältere Profile haben nur `orgId` ohne `organizations`-Eintrag – auch die müssen entfernt werden
      if (userData.organizations?.some(m => m.orgId === orgId)) {
        updates.organizations = remainingOrgs;
      }

      // If this was the active org, switch to another
      if (userData.orgId === orgId) {
        const next = remainingOrgs[0];
        updates.orgId = next?.orgId ?? null;
        updates.lastActiveOrg = next?.orgId ?? null;
        // Die globale Rolle gilt org-unabhängig (z. B. isAdmin() in den Rules) – nie als Admin zurücklassen
        updates.role = next?.role ?? 'sales_rep';
      }

      if (Object.keys(updates).length === 0) {
        throw new Error('User is not a member of this organization');
      }
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error removing user from organization:', error);
      throw error;
    }
  },

  // Update user role in organization
  async updateUserRole(
    userId: string,
    orgId: string,
    newRole: 'admin' | 'manager' | 'sales_rep'
  ): Promise<void> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data() as User;
      const updatedMemberships = userData.organizations?.map(m => 
        m.orgId === orgId ? { ...m, role: newRole } : m
      ) || [];
      
      const userRef = doc(db, 'users', userId);
      const updates: any = {
        organizations: updatedMemberships
      };
      
      // If this is the user's current/active organization, also update their primary role
      if (userData.orgId === orgId) {
        updates.role = newRole;
      }
      
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  // Switch active organization for user
  async switchOrganization(userId: string, orgId: string): Promise<void> {
    try {
      // Get user document to find their role in the new organization
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data() as User;
      
      // Super-Admin can switch to ANY organization
      if (userData.isSuperAdmin) {
        console.log('🌍 Super-Admin switching to organization:', orgId);
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          orgId,
          role: 'admin', // Super-Admin is always admin
          lastActiveOrg: orgId
        });
        return;
      }
      
      // Normal users must be members of the organization
      const membership = userData.organizations?.find(m => m.orgId === orgId);
      
      if (!membership) {
        throw new Error('User is not a member of this organization');
      }
      
      // Switch to new organization and update role to match membership
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        orgId,
        role: membership.role, // Use the role from the organization membership
        lastActiveOrg: orgId
      });
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  },

  // Transfer user to a new organization (remove from old, add to new)
  async transferUserToOrganization(
    userId: string,
    newOrgId: string,
    newRole: 'admin' | 'manager' | 'sales_rep' = 'sales_rep'
  ): Promise<void> {
    try {
      // Get user document
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data() as User;
      const currentOrgId = userData.orgId;
      
      // Get new organization details
      const newOrgDoc = await getDoc(doc(db, 'organizations', newOrgId));
      if (!newOrgDoc.exists()) {
        throw new Error('Target organization not found');
      }
      
      const newOrgData = newOrgDoc.data() as Organization;
      
      // Remove user from old organization if exists
      if (currentOrgId) {
        const oldMembership = userData.organizations?.find(m => m.orgId === currentOrgId);
        if (oldMembership) {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            organizations: arrayRemove(oldMembership)
          });
        }
      }
      
      // Create new membership
      const newMembership: OrganizationMembership = {
        orgId: newOrgId,
        orgName: newOrgData.name,
        role: newRole,
        joinedAt: new Date(),
        status: 'active'
      };
      
      // Add user to new organization and update primary orgId and role
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizations: arrayUnion(newMembership),
        orgId: newOrgId,
        role: newRole,
        lastActiveOrg: newOrgId
      });
      
      console.log(`✅ User ${userId} transferred from org ${currentOrgId} to ${newOrgId} with role ${newRole}`);
    } catch (error) {
      console.error('Error transferring user to organization:', error);
      throw error;
    }
  },

  // Get all users in an organization
  async getOrganizationUsers(orgId: string): Promise<User[]> {
    try {
      // Query users who have this org in their organizations array
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const orgUsers: User[] = [];
      usersSnapshot.docs.forEach(userDoc => {
        const userData = userDoc.data() as User;
        const isMember = userData.organizations?.some(
          m => m.orgId === orgId && m.status === 'active'
        );
        
        if (isMember) {
          orgUsers.push({
            id: userDoc.id,
            ...userData
          });
        }
      });
      
      return orgUsers;
    } catch (error) {
      console.error('Error getting organization users:', error);
      return [];
    }
  },

  // Check if user is admin of organization
  isOrgAdmin(user: User, orgId: string): boolean {
    const membership = user.organizations?.find(m => m.orgId === orgId);
    return membership?.role === 'admin' && membership?.status === 'active';
  },

  // Check if user is manager or admin of organization
  isOrgManager(user: User, orgId: string): boolean {
    const membership = user.organizations?.find(m => m.orgId === orgId);
    return (membership?.role === 'admin' || membership?.role === 'manager') && 
           membership?.status === 'active';
  },

  // Get user's role in specific organization
  getUserOrgRole(user: User, orgId: string): 'admin' | 'manager' | 'sales_rep' | null {
    const membership = user.organizations?.find(m => m.orgId === orgId);
    return membership?.status === 'active' ? membership.role : null;
  },

  // Delete organization (admin only)
  async deleteOrganization(orgId: string): Promise<void> {
    try {
      // Remove org from all users
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const batch = writeBatch(db);
      usersSnapshot.docs.forEach(userDoc => {
        const userData = userDoc.data() as User;
        const membership = userData.organizations?.find(m => m.orgId === orgId);
        
        if (membership) {
          const updatedMemberships = userData.organizations?.filter(
            m => m.orgId !== orgId
          ) || [];
          
          batch.update(doc(db, 'users', userDoc.id), {
            organizations: updatedMemberships
          });
          
          // Update active org if needed
          if (userData.orgId === orgId && updatedMemberships.length > 0) {
            batch.update(doc(db, 'users', userDoc.id), {
              orgId: updatedMemberships[0].orgId
            });
          }
        }
      });
      
      // Delete the organization document
      batch.delete(doc(db, 'organizations', orgId));
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }
};
